import { Heart, ShoppingBag, Trash2 } from "lucide-react";
import { useFavorites } from "@/lib/favorites";
import { Container } from "@/components/site/PageHeader";
import { SectionHeading } from "@/components/site/SectionHeading";
import { ListenLinks } from "@/components/site/ListenLinks";
import { ShareFavoritesButton } from "@/components/site/ShareFavoritesButton";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "");

export const TrackCard = ({ f, testId, children }) => (
  <li className="flex items-center gap-4 sm:gap-6 py-4 border-b border-white/10" data-testid={testId}>
    <img src={f.cover || "/brand/fallback-magazine.jpg"} alt="" loading="lazy" className="w-14 h-14 sm:w-16 sm:h-16 object-cover shrink-0 bg-[hsl(var(--surface))]" />
    <div className="min-w-0 flex-1">
      <p className="text-white font-semibold truncate">{f.title}</p>
      <p className="text-zinc-400 text-sm truncate">{f.artist}{f.album ? ` · ${f.album}` : ""}</p>
      {f.saved_at && <p className="text-zinc-400 text-xs mt-1">Ajouté le {fmt(f.saved_at)}</p>}
      <ListenLinks track={f} testId={`${testId}-listen`} />
    </div>
    {f.buy_link && (
      <a href={f.buy_link} target="_blank" rel="noreferrer" aria-label={`Acheter ${f.title}`} title="Acheter"
        className="w-10 h-10 shrink-0 grid place-items-center border border-white/15 text-zinc-300 hover:bg-primary hover:border-primary hover:text-white transition-colors" data-testid={`${testId}-buy`}>
        <ShoppingBag className="w-4 h-4" />
      </a>
    )}
    {children}
  </li>
);

const FavoriteRow = ({ f, onRemove }) => (
  <TrackCard f={f} testId="favorite-track">
    <button type="button" onClick={() => onRemove(f.key)} aria-label={`Retirer ${f.title}`} title="Retirer"
      className="w-10 h-10 shrink-0 grid place-items-center border border-white/15 text-zinc-300 hover:border-white hover:text-white transition-colors" data-testid="favorite-track-remove">
      <Trash2 className="w-4 h-4" />
    </button>
  </TrackCard>
);

export const FavoriteTracks = () => {
  const { list, remove } = useFavorites();
  return (
    <section id="favoris" className="py-24 md:py-32 border-b border-white/10 scroll-mt-20" data-testid="radio-favorites">
      <Container className="grid grid-cols-1 xl:grid-cols-[1fr_1.4fr] gap-12 xl:gap-20 [&>*]:min-w-0">
        <div>
          <SectionHeading top="Mes titres" bottom="favoris" outline testId="favorites-title" />
          <p className="text-zinc-300 text-base sm:text-lg leading-relaxed mt-8 max-w-md">
            Appuie sur le cœur dans le lecteur ou dans les titres diffusés pour garder un morceau. Ta liste reste sur cet appareil, sans compte.
          </p>
          {list.length > 0 && <p className="mt-6 text-xs uppercase tracking-[0.25em] font-bold text-primary" data-testid="favorites-count">{list.length} titre{list.length > 1 ? "s" : ""}</p>}
          {list.length > 0 && <ShareFavoritesButton list={list} />}
        </div>
        <div className="min-w-0">
          {list.length === 0 ? (
            <div className="border border-dashed border-white/15 p-10 flex items-center gap-5 text-zinc-400" data-testid="favorites-empty">
              <Heart className="w-6 h-6 text-primary shrink-0" /> Aucun favori pour l'instant.
            </div>
          ) : (
            <ol className="border-t border-white/10" data-testid="favorites-list">
              {list.map((f) => <FavoriteRow key={f.key} f={f} onRemove={remove} />)}
            </ol>
          )}
        </div>
      </Container>
    </section>
  );
};
